import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
    MenuItem,
    IconButton,
    Breadcrumbs,
    Link,
    Divider,
    useTheme,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, ErrorMessage, FieldArray, getIn, useFormikContext } from "formik";
import * as Yup from "yup";
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import { useAuth } from "../../../helpers/AuthProvider";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from "../../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";

// --- Configuration ---

// const cloudProviderList = [
//     { value: "gcp", label: "Google Cloud" },
//     { value: "aws", label: "Amazon Web Services" },
//     { value: "azure", label: "Microsoft Azure" },
// ];
const providerDisplayData = [
    { value: 'gcp', label: 'Google Cloud Platform', icon: '/assets/gcp_mini_icon.svg' },
    { value: 'aws', label: 'Amazon Web Services', icon: '/assets/aws_mini_icon.svg' },
    { value: 'azure', label: 'Microsoft Azure', icon: '/assets/azure_mini_icon.svg' }
];

const baseModelList = [
    { value: "google/gemma-7b-it", label: "google/gemma-7b-it" },
    { value: "meta-llama/Llama-3-8B-Instruct", label: "meta-llama/Llama-3-8B-Instruct" },
    // Other base models can be added here
];

const tuningMethodList = [
    { value: "lora", label: "LoRA" },
    { value: "qlora", label: "QLoRA" },
];

const dataFormatTemplateList = [
    { value: "conversational", label: "Conversational" },
    { value: "instruction", label: "Instruction" },
];

const trainingRegionList = {
    gcp: [
        { value: "africa-south1", label: "africa-south1" },
        { value: "asia-east1", label: "asia-east1" },
        { value: "asia-east2", label: "asia-east2" },
        { value: "asia-northeast1", label: "asia-northeast1" },
        { value: "asia-northeast2", label: "asia-northeast2" },
        { value: "asia-northeast3", label: "asia-northeast3" },
        { value: "asia-south1", label: "asia-south1" },
        { value: "asia-south2", label: "asia-south2" },
        { value: "asia-southeast1", label: "asia-southeast1" },
        { value: "asia-southeast2", label: "asia-southeast2" },
        { value: "australia-southeast1", label: "australia-southeast1" },
        { value: "australia-southeast2", label: "australia-southeast2" },
        { value: "europe-central2", label: "europe-central2" },
        { value: "europe-north1", label: "europe-north1" },
        { value: "europe-southwest1", label: "europe-southwest1" },
        { value: "europe-west1", label: "europe-west1" },
        { value: "europe-west10", label: "europe-west10" },
        { value: "europe-west12", label: "europe-west12" },
        { value: "europe-west2", label: "europe-west2" },
        { value: "europe-west3", label: "europe-west3" },
        { value: "europe-west4", label: "europe-west4" },
        { value: "europe-west6", label: "europe-west6" },
        { value: "europe-west8", label: "europe-west8" },
        { value: "europe-west9", label: "europe-west9" },
        { value: "me-central1", label: "me-central1" },
        { value: "me-central2", label: "me-central2" },
        { value: "me-west1", label: "me-west1" },
        { value: "northamerica-northeast1", label: "northamerica-northeast1" },
        { value: "northamerica-northeast2", label: "northamerica-northeast2" },
        { value: "southamerica-east1", label: "southamerica-east1" },
        { value: "southamerica-west1", label: "southamerica-west1" },
        { value: "us-central1", label: "us-central1" },
        { value: "us-east1", label: "us-east1" },
        { value: "us-east4", label: "us-east4" },
        { value: "us-east5", label: "us-east5" },
        { value: "us-south1", label: "us-south1" },
        { value: "us-west1", label: "us-west1" },
        { value: "us-west2", label: "us-west2" },
        { value: "us-west3", label: "us-west3" },
        { value: "us-west4", label: "us-west4" },
    ],
    aws: [
        { value: "us-east-2", label: "us-east-2" },
        { value: "us-east-1", label: "us-east-1" },
        { value: "us-west-1", label: "us-west-1" },
        { value: "us-west-2", label: "us-west-2" },
        { value: "af-south-1", label: "af-south-1" },
        { value: "ap-east-1", label: "ap-east-1" },
        { value: "ap-south-2", label: "ap-south-2" },
        { value: "ap-southeast-3", label: "ap-southeast-3" },
        { value: "ap-southeast-5", label: "ap-southeast-5" },
        { value: "ap-southeast-4", label: "ap-southeast-4" },
        { value: "ap-south-1", label: "ap-south-1" },
        { value: "ap-northeast-3", label: "ap-northeast-3" },
        { value: "ap-northeast-2", label: "ap-northeast-2" },
        { value: "ap-southeast-1", label: "ap-southeast-1" },
        { value: "ap-southeast-2", label: "ap-southeast-2" },
        { value: "ap-southeast-7", label: "ap-southeast-7" },
        { value: "ap-northeast-1", label: "ap-northeast-1" },
        { value: "ca-central-1", label: "ca-central-1" },
        { value: "ca-west-1", label: "ca-west-1" },
        { value: "eu-central-1", label: "eu-central-1" },
        { value: "eu-west-1", label: "eu-west-1" },
        { value: "eu-west-2", label: "eu-west-2" },
        { value: "eu-south-1", label: "eu-south-1" },
        { value: "eu-west-3", label: "eu-west-3" },
        { value: "eu-south-2", label: "eu-south-2" },
        { value: "eu-north-1", label: "eu-north-1" },
        { value: "eu-central-2", label: "eu-central-2" },
        { value: "il-central-1", label: "il-central-1" },
        { value: "mx-central-1", label: "mx-central-1" },
        { value: "me-south-1", label: "me-south-1" },
        { value: "me-central-1", label: "me-central-1" },
        { value: "sa-east-1", label: "sa-east-1" },
        { value: "us-gov-east-1", label: "us-gov-east-1" },
        { value: "us-gov-west-1", label: "us-gov-west-1" },
    ],
    azure: [
        { value: "eastus", label: "eastus" },
        { value: "eastus2", label: "eastus2" },
        { value: "westus", label: "westus" },
        { value: "westus2", label: "westus2" },
        { value: "centralus", label: "centralus" },
        { value: "northcentralus", label: "northcentralus" },
        { value: "southcentralus", label: "southcentralus" },
        { value: "westcentralus", label: "westcentralus" },
        { value: "canadacentral", label: "canadacentral" },
        { value: "canadaeast", label: "canadaeast" },
        { value: "brazilsouth", label: "brazilsouth" },
        { value: "northeurope", label: "northeurope" },
        { value: "westeurope", label: "westeurope" },
        { value: "uksouth", label: "uksouth" },
        { value: "ukwest", label: "ukwest" },
        { value: "francecentral", label: "francecentral" },
        { value: "germanywestcentral", label: "germanywestcentral" },
        { value: "switzerlandnorth", label: "switzerlandnorth" },
        { value: "uaenorth", label: "uaenorth" },
        { value: "japaneast", label: "japaneast" },
        { value: "japanwest", label: "japanwest" },
        { value: "koreacentral", label: "koreacentral" },
        { value: "koreasouth", label: "koreasouth" },
        { value: "southeastasia", label: "southeastasia" },
        { value: "australiaeast", label: "australiaeast" },
    ],
};

const acceleratorTypeList = {
    gcp: [
        { value: "NVIDIA_L4", label: "NVIDIA_L4" },
        { value: "NVIDIA_TESLA_T4", label: "NVIDIA_TESLA_T4" },
        { value: "NVIDIA_A100_40GB", label: "NVIDIA_A100_40GB" },
    ],
    aws: [
        { value: "ml.g5.xlarge", label: "ml.g5.xlarge (NVIDIA A10G)" },
        { value: "ml.g4dn.xlarge", label: "ml.g4dn.xlarge (NVIDIA T4)" },
        { value: "ml.p4d.24xlarge", label: "ml.p4d.24xlarge (NVIDIA A100)" },
    ],
    azure: [
        { value: "Standard_NC4as_T4_v3", label: "Standard_NC4as_T4_v3 (NVIDIA T4)" },
        { value: "Standard_NC6s_v3", label: "Standard_NC6s_v3 (NVIDIA V100)" },
        { value: "Standard_ND96asr_v4", label: "Standard_ND96asr_v4 (NVIDIA A100)" },
    ]
};

const dataPathPlaceholders = {
    gcp: "gs://my-gcp-bucket/data/train.jsonl",
    aws: "s3://my-aws-bucket/data/train.jsonl",
    azure: "https://myblob.blob.core.windows.net/data/train.jsonl",
};

// --- Interfaces ---
interface CostLabelPair {
    id?: string;
    cost: string;
    label: string;
}

interface FineTuningFormValues {
    name: string;
    application: string;
    costCenterValue: string;
    costLabelPairs: CostLabelPair[];
    cloudProvider: 'gcp' | 'aws' | 'azure';
    baseModel: string;
    tuningMethod: string;
    trainingDataPath: string;
    validationDataPath: string;
    dataFormatTemplate: string;
    trainingDatasetId: string;
    validationDatasetId: string;
    trainingRegion: string;
    acceleratorType: string;
    acceleratorCount: number;
    epochs: number;
    learningRate: number;
    batchSize: number;
    loraRank: number;
    loraAlpha: number;
}

// --- Validation Schema ---
const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required").max(100, "Max 100 characters"),
    application: Yup.string().required("Application is required"),
    costCenterValue: Yup.string().required("Cost Center value is required").max(63, "Max 63 characters"),
    costLabelPairs: Yup.array().of(Yup.object().shape({
        cost: Yup.string().required("Key is required").max(63, "Max 63 characters").notOneOf(["cost-center"], "'cost-center' is a reserved key."),
        label: Yup.string().required("Value is required").max(63, "Max 63 characters"),
    })),
    cloudProvider: Yup.string().required("This field is required"),
    baseModel: Yup.string().required("This field is required"),
    tuningMethod: Yup.string().required("This field is required"),
    trainingDataPath: Yup.string()
        .required("This field is required")
        .matches(/^(gs:\/\/|s3:\/\/|https:\/\/).+/, 'Invalid data path format for the selected cloud provider'),
    validationDataPath: Yup.string()
        .matches(/^(gs:\/\/|s3:\/\/|https:\/\/).+/, 'Invalid data path format for the selected cloud provider')
        .nullable(),
    dataFormatTemplate: Yup.string().required("This field is required"),
    trainingRegion: Yup.string().required("This field is required"),
    acceleratorType: Yup.string().required("This field is required"),
    acceleratorCount: Yup.number().integer("Must be an integer").min(1, "Must be at least 1").required("This field is required"),
    epochs: Yup.number().integer("Must be an integer").min(1, "Must be at least 1").required("This field is required"),
    learningRate: Yup.number().positive("Must be a positive number").required("This field is required"),
    batchSize: Yup.number().integer("Must be an integer").min(1, "Must be at least 1").required("This field is required"),
    loraRank: Yup.number().integer("Must be an integer").min(1, "Must be at least 1").required("This field is required"),
    loraAlpha: Yup.number().integer("Must be an integer").min(1, "Must be at least 1").required("This field is required"),
});


interface FineTuningFormProps {
    isEdit: boolean;
}
interface FineTuningFormContentProps {
    isEdit: boolean;
}

const CloudProviderFieldUpdater: React.FC = () => {
    const { values, setFieldValue, initialValues } = useFormikContext<FineTuningFormValues>();
    const selectedProvider = values.cloudProvider;

    useEffect(() => {
        // Don't run this on the initial form load
        if (values.cloudProvider === initialValues.cloudProvider) {
            return;
        }

        const availableRegions = trainingRegionList[selectedProvider];
        const availableAccelerators = acceleratorTypeList[selectedProvider];

        // Set the first available region for the new provider
        setFieldValue('trainingRegion', availableRegions?.[0]?.value || '');
        // Set the first available accelerator for the new provider
        setFieldValue('acceleratorType', availableAccelerators?.[0]?.value || '');

    }, [selectedProvider, setFieldValue, initialValues.cloudProvider]);

    return null;
};


const FineTuningFormContent: React.FC<FineTuningFormContentProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const { id: fineTuningIdParams } = useParams<{ id: string }>();
    const [initialFormValues, setInitialFormValues] = useState<FineTuningFormValues | null>(null);
    const [isLoading, setIsLoading] = useState(isEdit);
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const { user } = useAuth();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;
    const [applications, setApplications] = useState<any[]>([]);
    const [applicationNamesList, setApplicationNamesList] = useState<string[]>([]);

    useEffect(() => {
        queryGraphql(`query { applications { id, applicationName, status } }`)
            .then(data => {
                if (data.applications) {
                    const activeApps = data.applications.filter((app: any) => app.status === 'active');
                    setApplications(activeApps);
                    setApplicationNamesList(Array.from(new Set(activeApps.map((app: any) => app.applicationName))) as string[]);
                }
            }).catch(err => console.error("Error fetching applications", err));
    }, []);

    useEffect(() => {
        if (isEdit && fineTuningIdParams) {
            setIsLoading(true);
            queryGraphql(`
                query GetFineTuning($getFineTuningId: ID!) {
                  getFineTuning(id: $getFineTuningId) {
                    id
                    name
                    application { id, applicationName }
                    cloudProvider
                    costLabelPairs { cost, label }
                    details {
                      fineTuning {
                        baseModel, tuningMethod, trainingDataPath, validationDataPath,
                        dataFormatTemplate, trainingDatasetId, validationDatasetId, trainingRegion,
                        acceleratorType, acceleratorCount, epochs, learningRate, batchSize,
                        loraRank, loraAlpha
                      }
                    }
                  }
                }
            `, { getFineTuningId: fineTuningIdParams })
                .then(response => {
                    const ft = response.getFineTuning;
                    if (ft && ft.details && ft.details.fineTuning) {
                        const allPairs: { cost: string, label: string }[] = ft.costLabelPairs || [];
                        const costCenterPair = allPairs.find(p => p.cost === "cost-center");
                        const dynamicPairs = allPairs
                            .filter(p => p.cost !== "cost-center")
                            .map((pair, index) => ({ ...pair, id: `clp-edit-${index}` }));

                        setInitialFormValues({
                            name: ft.name || "",
                            application: ft.application?.applicationName || "",
                            costCenterValue: costCenterPair ? costCenterPair.label : "",
                            costLabelPairs: dynamicPairs,
                            cloudProvider: ft.cloudProvider || "gcp",
                            ...ft.details.fineTuning
                        });
                    } else {
                        console.error("Fine Tuning Job not found or data is incomplete:", fineTuningIdParams);
                        navigate("/aisolutions/finetuning");
                    }
                })
                .catch(err => {
                    console.error("Error fetching fine tuning job:", err);
                    navigate("/aisolutions/finetuning");
                })
                .finally(() => setIsLoading(false));
        } else {
            setInitialFormValues({
                name: "",
                application: "",
                costCenterValue: "",
                costLabelPairs: [],
                cloudProvider: "gcp",
                baseModel: "google/gemma-7b-it",
                tuningMethod: "lora",
                trainingDataPath: "",
                validationDataPath: "",
                dataFormatTemplate: "conversational",
                trainingDatasetId: "",
                validationDatasetId: "",
                trainingRegion: "us-central1",
                acceleratorType: "NVIDIA_L4",
                acceleratorCount: 1,
                epochs: 3,
                learningRate: 0.0002,
                batchSize: 4,
                loraRank: 4,
                loraAlpha: 16,
            });
            setIsLoading(false);
        }
    }, [isEdit, fineTuningIdParams, navigate]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "50vh" }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading Fine Tuning Job Data...</Typography>
            </Box>
        );
    }

    if (!initialFormValues) {
        return <Typography color="error" sx={{ p: 3 }}>Could not initialize form.</Typography>;
    }


    return (
        <Container
            disableGutters
            maxWidth={false}
            sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}
        >
            <Formik
                initialValues={initialFormValues}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={async (values, { setSubmitting, setStatus }) => {
                    setSubmitting(true);

                    const finalCostLabelPairs = [
                        { cost: "cost-center", label: values.costCenterValue },
                        ...values.costLabelPairs.map(({ cost, label }) => ({ cost, label }))
                    ];

                    const selectedApplication = applications.find(app => app.applicationName === values.application);

                    const apiPayload = {
                        name: values.name,
                        application: selectedApplication ? selectedApplication.id : undefined,
                        costLabelPairs: finalCostLabelPairs,
                        cloudProvider: values.cloudProvider,
                        details: {
                            fineTuning: {
                                baseModel: values.baseModel,
                                tuningMethod: values.tuningMethod,
                                trainingDataPath: values.trainingDataPath,
                                validationDataPath: values.validationDataPath,
                                dataFormatTemplate: values.dataFormatTemplate,
                                trainingDatasetId: values.trainingDatasetId,
                                validationDatasetId: values.validationDatasetId,
                                trainingRegion: values.trainingRegion,
                                acceleratorType: values.acceleratorType,
                                acceleratorCount: String(values.acceleratorCount),
                                epochs: String(values.epochs),
                                learningRate: String(values.learningRate),
                                batchSize: String(values.batchSize),
                                loraRank: String(values.loraRank),
                                loraAlpha: String(values.loraAlpha),
                            }
                        }
                    };

                    let mutationString;
                    let variables: { input: any };
                    let mutationResultKey;

                    if (isEdit && fineTuningIdParams) {
                        mutationString = `mutation UpdateSolutionFineTuning($input: UpdateSolutionInput!) {
                          updateSolutionFineTuning(input: $input) { id, name }
                        }`;
                        variables = { input: { id: fineTuningIdParams, ...apiPayload } };
                        mutationResultKey = 'updateSolutionFineTuning';
                    } else {
                        mutationString = `mutation CreateSolutionFineTuning($input: CreateSolutionInput!) {
                          createSolutionFineTuning(input: $input) { id, name }
                        }`;
                        variables = { input: apiPayload };
                        mutationResultKey = 'createSolutionFineTuning';
                    }

                    try {
                        const responseData = await queryGraphql(mutationString, variables);
                        
                        if (!responseData) {
                            setStatus({ error: "The API call returned an undefined response. Please check the network request and the `queryGraphql` helper function." });
                            setSubmitting(false);
                            return; 
                        }
                        
                        if (responseData[mutationResultKey]) {
                            navigate("/aisolutions/finetuning");
                        } else {
                            const errorMsg = responseData.errors ? 
                                JSON.stringify(responseData.errors) : 
                                'API call did not return the expected data. Check the server response.';
                            setStatus({ error: `Failed to ${isEdit ? 'update' : 'create'} job. ${errorMsg}` });
                        }
                    } catch (error: any) {
                        setStatus({ error: `An unexpected error occurred: ${error.message}` });
                    }
                    setSubmitting(false);
                }}
            >
                {({ handleChange, handleBlur, values, errors, setFieldValue,touched, isValid, dirty }) => (
                    <Form id="fine-tuning-form">
                        <CloudProviderFieldUpdater />
                        <Box sx={{ mb: 4 }}>
                            <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                                <Link underline="hover" color="inherit" href="/aisolutions/finetuning" onClick={(e) => { e.preventDefault(); navigate("/aisolutions/finetuning"); }}>
                                    Fine Tuning Jobs
                                </Link>
                                <Typography color="text.primary">
                                    {isEdit ? "Edit Fine Tuning Job" : "New Fine Tuning Job"}
                                </Typography>
                            </Breadcrumbs>
                            <Box display="flex" alignItems="center" mt={2}>
                                <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/aisolutions/finetuning")}>
                                    <ArrowBackIosNewIcon fontSize="small" />
                                </IconButton>
                                <Typography fontFamily="Gilroy-Bold" fontSize="18px">
                                    {isEdit ? "Edit Fine Tuning Job" : "Add New Fine Tuning Job"}
                                </Typography>
                            </Box>
                        </Box>
                        <Grid container>
                            <Grid item xs={1}></Grid>
                            <Grid item xs={10}>
                                <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                    <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
                                        Cloud Provider
                                    </Typography>
                                    <Divider sx={{ marginBottom: "24px" }} />
                                    <Grid container spacing={4}>
                                        <Grid item xs={12} sm={12}>
                                            <CompulsoryFieldsLabel fieldName="Cloud Provider" />
                                           
                                                <ToggleButtonGroup
                                            value={values.cloudProvider}
                                            exclusive
                                            onChange={(event, newProvider) => {
                                                if (newProvider !== null) {
                                                    setFieldValue('cloudProvider', newProvider);
                                                }
                                            }}
                                            aria-label="cloud provider"
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                flexWrap: 'wrap'
                                            }}
                                        >
                                            {providerDisplayData.map((p) => (
                                                <ToggleButton
                                                    key={p.value}
                                                    value={p.value}
                                                    aria-label={p.label}
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1,
                                                        alignItems: 'center',
                                                        textTransform: 'none',
                                                        fontFamily: 'Gilroy-Medium',
                                                        borderRadius: '24px !important',
                                                        border: '1px solid #ccc !important',
                                                        px: 2,
                                                        py: 1,
                                                        '&.Mui-selected': {
                                                            borderColor: '#000 !important',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                        },
                                                        '&.Mui-selected:hover': {
                                                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                                        }
                                                    }}
                                                >
                                                    <img src={p.icon} alt={`${p.label} icon`} style={{ width: '20px', height: '20px' }} />
                                                    {p.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                        
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="cloudProvider" />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </div>

                                {/* --- Fine Tuning Job Details Section --- */}
                                <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                    <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
                                        Fine Tuning Job Details
                                    </Typography>
                                    <Divider sx={{ marginBottom: "24px" }} />
                                    <Grid container spacing={4}>
                                        <Grid item xs={12} sm={6}>
                                            <CompulsoryFieldsLabel fieldName="Fine Tuning Job Name" />
                                            <TextField fullWidth name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Enter a name for your fine-tuning job" variant="filled" hiddenLabel error={touched.name && Boolean(errors.name)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="name" />
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <CompulsoryFieldsLabel fieldName="Application" />
                                            <TextField select fullWidth name="application" value={values.application} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.application && Boolean(errors.application)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                                {applicationNamesList.map(appName => (<MenuItem key={appName} value={appName}>{appName}</MenuItem>))}
                                            </TextField>
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="application" />
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <CompulsoryFieldsLabel fieldName="Base Model to Fine Tune" />
                                            <TextField select fullWidth name="baseModel" value={values.baseModel} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.baseModel && Boolean(errors.baseModel)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                                {baseModelList.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="baseModel" />
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <CompulsoryFieldsLabel fieldName="Tuning Method" />
                                            <TextField select fullWidth name="tuningMethod" value={values.tuningMethod} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.tuningMethod && Boolean(errors.tuningMethod)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                                {tuningMethodList.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="tuningMethod" />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </div>

                                {/* --- Labels Section --- */}
                                <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                    <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Labels</Typography>
                                    <Divider sx={{ mb: "24px" }} />
                                    <Grid container spacing={3} sx={{ mb: "16px" }}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography>
                                            <TextField fullWidth value="cost-center" disabled variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px", backgroundColor: "#f0f0f0" } }} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <CompulsoryFieldsLabel fieldName="Value" />
                                            <TextField fullWidth name="costCenterValue" value={values.costCenterValue} onChange={handleChange} onBlur={handleBlur} placeholder="Enter cost center value" variant="filled" hiddenLabel error={touched.costCenterValue && Boolean(errors.costCenterValue)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" /></div>
                                        </Grid>
                                    </Grid>
                                    <FieldArray
                                        name="costLabelPairs"
                                        render={arrayHelpers => (
                                            <div>
                                                {values.costLabelPairs.map((pair, index) => (
                                                    <Grid container spacing={2} key={pair.id || index} sx={{ mb: "16px" }} alignItems="center">
                                                        <Grid item xs={5}>
                                                            <CompulsoryFieldsLabel fieldName="Key" />
                                                            <TextField fullWidth name={`costLabelPairs.${index}.cost`} value={pair.cost} onChange={handleChange} onBlur={handleBlur} placeholder="Enter key" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].cost`) && !!getIn(errors, `costLabelPairs[${index}].cost`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                            <div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.cost`} /></div>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <CompulsoryFieldsLabel fieldName="Value" />
                                                            <TextField fullWidth name={`costLabelPairs.${index}.label`} value={pair.label} onChange={handleChange} onBlur={handleBlur} placeholder="Enter value" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].label`) && !!getIn(errors, `costLabelPairs[${index}].label`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                            <div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.label`} /></div>
                                                        </Grid>
                                                        <Grid item xs={1}><IconButton onClick={() => arrayHelpers.remove(index)} sx={{ mt: 2 }}><img alt="remove" src="/assets/close-icon.svg" /></IconButton></Grid>
                                                    </Grid>
                                                ))}
                                                <Button variant="text" sx={{ color: "#000000", textTransform: "none" }} onClick={() => arrayHelpers.push({ cost: "", label: "", id: `new-${Math.random()}` })}>+ Add Key Value Pair</Button>
                                            </div>
                                        )}
                                    />
                                </div>


                                {/* --- Dataset Configuration Section --- */}
                                <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                    <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
                                        Dataset Configuration
                                    </Typography>
                                    <Divider sx={{ marginBottom: "24px" }} />
                                    <Grid container spacing={4}>
                                        <Grid item xs={12} sm={6}>
                                            <CompulsoryFieldsLabel fieldName="Training Data Path" />
                                            <TextField fullWidth name="trainingDataPath" value={values.trainingDataPath} onChange={handleChange} onBlur={handleBlur} placeholder={dataPathPlaceholders[values.cloudProvider]} variant="filled" hiddenLabel error={touched.trainingDataPath && Boolean(errors.trainingDataPath)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="trainingDataPath" />
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography fontFamily="Gilroy-Regular" fontSize="14px" color="#232324" ml="4px" mb="4px">Validation Data Path (optional)</Typography>
                                            <TextField fullWidth name="validationDataPath" value={values.validationDataPath} onChange={handleChange} onBlur={handleBlur} placeholder={dataPathPlaceholders[values.cloudProvider]} variant="filled" hiddenLabel error={touched.validationDataPath && Boolean(errors.validationDataPath)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="validationDataPath" />
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <CompulsoryFieldsLabel fieldName="Data Format Template" />
                                            <TextField select fullWidth name="dataFormatTemplate" value={values.dataFormatTemplate} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                                {dataFormatTemplateList.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="dataFormatTemplate" />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </div>

                                {/* --- Training Infrastructure and Hardware Section --- */}
                                <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                    <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
                                        Training Infrastructure and Hardware
                                    </Typography>
                                    <Divider sx={{ marginBottom: "24px" }} />
                                    <Grid container spacing={4}>
                                        <Grid item xs={12} sm={6}>
                                            <CompulsoryFieldsLabel fieldName="Training Region" />
                                            <TextField select fullWidth name="trainingRegion" value={values.trainingRegion} onChange={handleChange} onBlur={handleBlur} disabled={!values.cloudProvider} variant="filled" hiddenLabel error={touched.trainingRegion && Boolean(errors.trainingRegion)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                                {(trainingRegionList[values.cloudProvider] || []).map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="trainingRegion" />
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <CompulsoryFieldsLabel fieldName="Accelerator (GPU) Type" />
                                            <TextField select fullWidth name="acceleratorType" value={values.acceleratorType} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.acceleratorType && Boolean(errors.acceleratorType)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                                {(acceleratorTypeList[values.cloudProvider] || []).map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="acceleratorType" />
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <CompulsoryFieldsLabel fieldName="Accelerator Count" />
                                            <TextField fullWidth type="number" name="acceleratorCount" value={values.acceleratorCount} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="acceleratorCount" />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </div>


                                {/* --- Hyperparameters Section --- */}
                                <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                    <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
                                        Hyperparameters (Advanced Tuning)
                                    </Typography>
                                    <Divider sx={{ marginBottom: "24px" }} />
                                    <Grid container spacing={4}>
                                        <Grid item xs={12} sm={4}>
                                            <CompulsoryFieldsLabel fieldName="Epochs" />
                                            <TextField fullWidth type="number" name="epochs" value={values.epochs} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="epochs" />
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <CompulsoryFieldsLabel fieldName="Learning Rate" />
                                            <TextField fullWidth type="number" name="learningRate" value={values.learningRate} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="learningRate" />
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <CompulsoryFieldsLabel fieldName="Batch Size" />
                                            <TextField fullWidth type="number" name="batchSize" value={values.batchSize} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="batchSize" />
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <CompulsoryFieldsLabel fieldName="LoRA Rank (r)" />
                                            <TextField fullWidth type="number" name="loraRank" value={values.loraRank} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="loraRank" />
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <CompulsoryFieldsLabel fieldName="LoRA Alpha (α)" />
                                            <TextField fullWidth type="number" name="loraAlpha" value={values.loraAlpha} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="loraAlpha" />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </div>


                                {/* --- Action Buttons --- */}
                                <FormControlLabel
                                    control={<Checkbox checked={confirmationChecked} onChange={(e) => setConfirmationChecked(e.target.checked)} name="confirmationCheckbox" sx={{ color: 'black', '&.Mui-checked': { color: 'black' } }} />}
                                    label={<Typography sx={{ color: 'black' }}>I confirm that the details provided are correct and I wish to proceed.</Typography>}
                                    sx={{ mt: 2.5, display: 'flex' }}
                                />
                                <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                                    <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, }} variant="outlined" size="large" onClick={() => navigate("/aisolutions/finetuning")}>
                                        Cancel
                                    </Button>
                                    <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "white", color: "black", borderColor: "black", }, textTransform: "none", px: 4, }} variant="contained" size="large" type="submit" disabled={!(isValid && dirty && confirmationChecked) || isViewer}>
                                        {isEdit ? "Save Changes" : "Add New Fine Tuning Job"}
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid item xs={1}></Grid>
                        </Grid>
                        <MiniBot />
                    </Form>
                )}
            </Formik>
        </Container>
    );
};

const FineTuningForm: React.FC<FineTuningFormProps> = ({ isEdit }) => {
    const theme = useTheme();

    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
    };

    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                index={"aisolutions-finetuning"}
                onStateChange={handleDrawerStateChange}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    marginLeft: `${currentMarginLeft}vh`,
                }}
            >
                <FineTuningFormContent isEdit={isEdit} />
            </Box>
        </Box>
    );
};

export default FineTuningForm;



// import React, { useState, useEffect } from "react";
// import {
//     Box,
//     Button,
//     Container,
//     Grid,
//     TextField,
//     Typography,
//     MenuItem,
//     IconButton,
//     Breadcrumbs,
//     Link,
//     Divider,
//     useTheme,
//     CircularProgress,
//     FormControlLabel,
//     Checkbox,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import { useNavigate, useParams } from "react-router-dom";
// import { Formik, Form, ErrorMessage, FieldArray, getIn } from "formik";
// import * as Yup from "yup";
// import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
// import { useAuth } from "../../../helpers/AuthProvider";
// import queryGraphql from "../../../helpers/ApiInterface";
// import MiniBot from "../../MiniBot";
// import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";

// // --- Configuration ---

// const cloudProviderList = [
//     { value: "gcp", label: "Google Cloud" },
//     // Other cloud providers can be added here in the future
// ];

// const baseModelList = [
//     { value: "google/gemma-7b-it", label: "google/gemma-7b-it" },
//     { value: "meta-llama/Llama-3-8B-Instruct", label: "meta-llama/Llama-3-8B-Instruct" },
//     // Other base models can be added here
// ];

// const tuningMethodList = [
//     { value: "lora", label: "LoRA" },
//     { value: "qlora", label: "QLoRA" },
// ];

// const dataFormatTemplateList = [
//     { value: "conversational", label: "Conversational" },
//     { value: "instruction", label: "Instruction" },
// ];

// const trainingRegionList = {
//     gcp: [
//         { value: "asia-east1", label: "asia-east1" },
//         { value: "asia-east2", label: "asia-east2" },
//         { value: "asia-northeast1", label: "asia-northeast1" },
//         { value: "asia-northeast2", label: "asia-northeast2" },
//         { value: "asia-northeast3", label: "asia-northeast3" },
//         { value: "asia-south1", label: "asia-south1" },
//         { value: "asia-south2", label: "asia-south2" },
//         { value: "asia-southeast1", label: "asia-southeast1" },
//         { value: "asia-southeast2", label: "asia-southeast2" },
//         { value: "australia-southeast1", label: "australia-southeast1" },
//         { value: "australia-southeast2", label: "australia-southeast2" },
//         { value: "europe-central2", label: "europe-central2" },
//         { value: "europe-north1", label: "europe-north1" },
//         { value: "europe-southwest1", label: "europe-southwest1" },
//         { value: "europe-west1", label: "europe-west1" },
//         { value: "europe-west2", label: "europe-west2" },
//         { value: "europe-west3", label: "europe-west3" },
//         { value: "europe-west4", label: "europe-west4" },
//         { value: "europe-west6", label: "europe-west6" },
//         { value: "europe-west8", label: "europe-west8" },
//         { value: "europe-west9", label: "europe-west9" },
//         { value: "europe-west12", label: "europe-west12" },
//         { value: "me-central1", label: "me-central1" },
//         { value: "me-central2", label: "me-central2" },
//         { value: "me-west1", label: "me-west1" },
//         { value: "northamerica-northeast1", label: "northamerica-northeast1" },
//         { value: "northamerica-northeast2", label: "northamerica-northeast2" },
//         { value: "southamerica-east1", label: "southamerica-east1" },
//         { value: "southamerica-west1", label: "southamerica-west1" },
//         { value: "us-central1", label: "us-central1" },
//         { value: "us-east1", label: "us-east1" },
//         { value: "us-east4", label: "us-east4" },
//         { value: "us-east5", label: "us-east5" },
//         { value: "us-south1", label: "us-south1" },
//         { value: "us-west1", label: "us-west1" },
//         { value: "us-west2", label: "us-west2" },
//         { value: "us-west3", label: "us-west3" },
//         { value: "us-west4", label: "us-west4" },
//     ],
// };

// const acceleratorTypeList = [
//     { value: "NVIDIA_L4", label: "NVIDIA_L4" },
//     { value: "NVIDIA_TESLA_T4", label: "NVIDIA_TESLA_T4" },
//     { value: "NVIDIA_A100_40GB", label: "NVIDIA_A100_40GB" },
// ];

// // --- Interfaces ---
// interface CostLabelPair {
//     id?: string;
//     cost: string;
//     label: string;
// }

// interface FineTuningFormValues {
//     name: string;
//     application: string;
//     costCenterValue: string;
//     costLabelPairs: CostLabelPair[];
//     cloudProvider: string;
//     baseModel: string;
//     tuningMethod: string;
//     trainingDataPath: string;
//     validationDataPath: string;
//     dataFormatTemplate: string;
//     trainingDatasetId: string;
//     validationDatasetId: string;
//     trainingRegion: string;
//     acceleratorType: string;
//     acceleratorCount: number;
//     epochs: number;
//     learningRate: number;
//     batchSize: number;
//     loraRank: number;
//     loraAlpha: number;
// }

// // --- Validation Schema (Aligned with GraphQL schema and form values) ---
// const validationSchema = Yup.object().shape({
//     name: Yup.string().required("This field is required").max(100, "Max 100 characters"),
//     application: Yup.string().required("Application is required"),
//     costCenterValue: Yup.string().required("Cost Center value is required").max(63, "Max 63 characters"),
//     costLabelPairs: Yup.array().of(Yup.object().shape({
//         cost: Yup.string().required("Key is required").max(63, "Max 63 characters").notOneOf(["cost-center"], "'cost-center' is a reserved key."),
//         label: Yup.string().required("Value is required").max(63, "Max 63 characters"),
//     })),
//     cloudProvider: Yup.string().required("This field is required"),
//     baseModel: Yup.string().required("This field is required"),
//     tuningMethod: Yup.string().required("This field is required"),
//     trainingDataPath: Yup.string().required("This field is required"),
//     validationDataPath: Yup.string(),
//     dataFormatTemplate: Yup.string().required("This field is required"),
//     trainingRegion: Yup.string().required("This field is required"),
//     acceleratorType: Yup.string().required("This field is required"),
//     acceleratorCount: Yup.number().integer("Must be an integer").min(1, "Must be at least 1").required("This field is required"),
//     epochs: Yup.number().integer("Must be an integer").min(1, "Must be at least 1").required("This field is required"),
//     learningRate: Yup.number().positive("Must be a positive number").required("This field is required"),
//     batchSize: Yup.number().integer("Must be an integer").min(1, "Must be at least 1").required("This field is required"),
//     loraRank: Yup.number().integer("Must be an integer").min(1, "Must be at least 1").required("This field is required"),
//     loraAlpha: Yup.number().integer("Must be an integer").min(1, "Must be at least 1").required("This field is required"),
// });


// interface FineTuningFormProps {
//     isEdit: boolean;
// }
// interface FineTuningFormContentProps {
//     isEdit: boolean;
// }

// const FineTuningFormContent: React.FC<FineTuningFormContentProps> = ({ isEdit }) => {
//     const navigate = useNavigate();
//     const { id: fineTuningIdParams } = useParams<{ id: string }>();
//     const [initialFormValues, setInitialFormValues] = useState<FineTuningFormValues | null>(null);
//     const [isLoading, setIsLoading] = useState(isEdit);
//     const [confirmationChecked, setConfirmationChecked] = useState(false);
//     const { user } = useAuth();
//     const isViewer = user.roles?.some((role: string) => role === "viewer") || false;
//     const [applications, setApplications] = useState<any[]>([]);
//     const [applicationNamesList, setApplicationNamesList] = useState<string[]>([]);

//     // Effect to fetch applications for the dropdown
//     useEffect(() => {
//         queryGraphql(`query { applications { id, applicationName, status } }`)
//             .then(data => {
//                 if (data.applications) {
//                     const activeApps = data.applications.filter((app: any) => app.status === 'active');
//                     setApplications(activeApps);
//                     setApplicationNamesList(Array.from(new Set(activeApps.map((app: any) => app.applicationName))) as string[]);
//                 }
//             }).catch(err => console.error("Error fetching applications", err));
//     }, []);

//     // Effect to fetch data for editing or set defaults for creating
//     useEffect(() => {
//         if (isEdit && fineTuningIdParams) {
//             setIsLoading(true);
//             queryGraphql(`
//                 query GetFineTuning($getFineTuningId: ID!) {
//                   getFineTuning(id: $getFineTuningId) {
//                     id
//                     name
//                     application { id, applicationName }
//                     cloudProvider
//                     costLabelPairs { cost, label }
//                     details {
//                       fineTuning {
//                         baseModel 
//                         tuningMethod 
//                         trainingDataPath 
//                         validationDataPath
//                         dataFormatTemplate
//                         trainingDatasetId
//                         validationDatasetId 
//                         trainingRegion 
//                         acceleratorType 
//                         acceleratorCount
//                         epochs 
//                         learningRate 
//                         batchSize 
//                         loraRank 
//                         loraAlpha
//                       }
//                     }
//                   }
//                 }
//             `, { getFineTuningId: fineTuningIdParams })
//                 .then(response => {
//                     const ft = response.getFineTuning;
//                     if (ft && ft.details && ft.details.fineTuning) {
//                         const allPairs: { cost: string, label: string }[] = ft.costLabelPairs || [];
//                         const costCenterPair = allPairs.find(p => p.cost === "cost-center");
//                         const dynamicPairs = allPairs
//                             .filter(p => p.cost !== "cost-center")
//                             .map((pair, index) => ({ ...pair, id: `clp-edit-${index}` }));

//                         setInitialFormValues({
//                             name: ft.name || "",
//                             application: ft.application?.applicationName || "",
//                             costCenterValue: costCenterPair ? costCenterPair.label : "",
//                             costLabelPairs: dynamicPairs,
//                             cloudProvider: ft.cloudProvider || "gcp",
//                             ...ft.details.fineTuning
//                         });
//                     } else {
//                         console.error("Fine Tuning Job not found or data is incomplete:", fineTuningIdParams);
//                         navigate("/aisolutions/finetuning");
//                     }
//                 })
//                 .catch(err => {
//                     console.error("Error fetching fine tuning job:", err);
//                     navigate("/aisolutions/finetuning");
//                 })
//                 .finally(() => setIsLoading(false));
//         } else {
//             setInitialFormValues({
//                 name: "",
//                 application: "",
//                 costCenterValue: "",
//                 costLabelPairs: [],
//                 cloudProvider: "gcp",
//                 baseModel: "google/gemma-7b-it",
//                 tuningMethod: "lora",
//                 trainingDataPath: "",
//                 validationDataPath: "",
//                 dataFormatTemplate: "conversational",
//                 trainingDatasetId: "",
//                 validationDatasetId: "",
//                 trainingRegion: "us-central1",
//                 acceleratorType: "NVIDIA_L4",
//                 acceleratorCount: 1,
//                 epochs: 3,
//                 learningRate: 0.0002,
//                 batchSize: 4,
//                 loraRank: 4,
//                 loraAlpha: 16,
//             });
//             setIsLoading(false);
//         }
//     }, [isEdit, fineTuningIdParams, navigate]);

//     if (isLoading) {
//         return (
//             <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "50vh" }}>
//                 <CircularProgress />
//                 <Typography sx={{ ml: 2 }}>Loading Fine Tuning Job Data...</Typography>
//             </Box>
//         );
//     }

//     if (!initialFormValues) {
//         return <Typography color="error" sx={{ p: 3 }}>Could not initialize form.</Typography>;
//     }


//     return (
//         <Container
//             disableGutters
//             maxWidth={false}
//             sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}
//         >
//             <Formik
//                 initialValues={initialFormValues}
//                 validationSchema={validationSchema}
//                 enableReinitialize={true}
//                 onSubmit={async (values, { setSubmitting, setStatus }) => {
//                     setSubmitting(true);

//                     const finalCostLabelPairs = [
//                         { cost: "cost-center", label: values.costCenterValue },
//                         ...values.costLabelPairs.map(({ cost, label }) => ({ cost, label }))
//                     ];

//                     const selectedApplication = applications.find(app => app.applicationName === values.application);

//                     const apiPayload = {
//                         name: values.name,
//                         application: selectedApplication ? selectedApplication.id : undefined,
//                         costLabelPairs: finalCostLabelPairs,
//                         cloudProvider: values.cloudProvider,
//                         details: {
//                             fineTuning: {
//                                 baseModel: values.baseModel,
//                                 tuningMethod: values.tuningMethod,
//                                 trainingDataPath: values.trainingDataPath,
//                                 validationDataPath: values.validationDataPath,
//                                 dataFormatTemplate: values.dataFormatTemplate,
//                                 trainingDatasetId: values.trainingDatasetId,
//                                 validationDatasetId: values.validationDatasetId,
//                                 trainingRegion: values.trainingRegion,
//                                 acceleratorType: values.acceleratorType,
//                                 // MODIFIED: Convert all numeric fields to strings for the backend
//                                 acceleratorCount: String(values.acceleratorCount),
//                                 epochs: String(values.epochs),
//                                 learningRate: String(values.learningRate),
//                                 batchSize: String(values.batchSize),
//                                 loraRank: String(values.loraRank),
//                                 loraAlpha: String(values.loraAlpha),
//                             }
//                         }
//                     };

//                     let mutationString;
//                     let variables: { input: any };
//                     let mutationResultKey;

//                     if (isEdit && fineTuningIdParams) {
//                         mutationString = `mutation UpdateSolutionFineTuning($input: UpdateSolutionInput!) {
//                           updateSolutionFineTuning(input: $input) { id, name }
//                         }`;
//                         variables = { input: { id: fineTuningIdParams, ...apiPayload } };
//                         mutationResultKey = 'updateSolutionFineTuning';
//                     } else {
//                         mutationString = `mutation CreateSolutionFineTuning($input: CreateSolutionInput!) {
//                           createSolutionFineTuning(input: $input) { id, name }
//                         }`;
//                         variables = { input: apiPayload };
//                         mutationResultKey = 'createSolutionFineTuning';
//                     }

//                     try {
//                         const responseData = await queryGraphql(mutationString, variables);
                        
//                         if (!responseData) {
//                             setStatus({ error: "The API call returned an undefined response. Please check the network request and the `queryGraphql` helper function." });
//                             setSubmitting(false);
//                             return; 
//                         }
                        
//                         if (responseData[mutationResultKey]) {
//                             navigate("/aisolutions/finetuning");
//                         } else {
//                             const errorMsg = responseData.errors ? 
//                                 JSON.stringify(responseData.errors) : 
//                                 'API call did not return the expected data. Check the server response.';
//                             setStatus({ error: `Failed to ${isEdit ? 'update' : 'create'} job. ${errorMsg}` });
//                         }
//                     } catch (error: any) {
//                         setStatus({ error: `An unexpected error occurred: ${error.message}` });
//                     }
//                     setSubmitting(false);
//                 }}
//             >
//                 {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
//                     <Form id="fine-tuning-form">
//                         <Box sx={{ mb: 4 }}>
//                             <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
//                                 <Link underline="hover" color="inherit" href="/aisolutions/finetuning" onClick={(e) => { e.preventDefault(); navigate("/aisolutions/finetuning"); }}>
//                                     Fine Tuning Jobs
//                                 </Link>
//                                 <Typography color="text.primary">
//                                     {isEdit ? "Edit Fine Tuning Job" : "New Fine Tuning Job"}
//                                 </Typography>
//                             </Breadcrumbs>
//                             <Box display="flex" alignItems="center" mt={2}>
//                                 <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/aisolutions/finetuning")}>
//                                     <ArrowBackIosNewIcon fontSize="small" />
//                                 </IconButton>
//                                 <Typography fontFamily="Gilroy-Bold" fontSize="18px">
//                                     {isEdit ? "Edit Fine Tuning Job" : "Add a New Fine Tuning Job"}
//                                 </Typography>
//                             </Box>
//                         </Box>
//                         <Grid container>
//                             <Grid item xs={1}></Grid>
//                             <Grid item xs={10}>
//                                 <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                     <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
//                                         Cloud Provider
//                                     </Typography>
//                                     <Divider sx={{ marginBottom: "24px" }} />
//                                     <Grid container spacing={4}>
//                                         <Grid item xs={12} sm={12}>
//                                             <CompulsoryFieldsLabel fieldName="Cloud Provider" />
//                                             <TextField select fullWidth name="cloudProvider" value={values.cloudProvider} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                 {cloudProviderList.map((option) => (
//                                                     <MenuItem key={option.value} value={option.value}>
//                                                         {option.label}
//                                                     </MenuItem>
//                                                 ))}
//                                             </TextField>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="cloudProvider" />
//                                             </div>
//                                         </Grid>
//                                     </Grid>
//                                 </div>

//                                 {/* --- Fine Tuning Job Details Section --- */}
//                                 <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                     <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
//                                         Fine Tuning Job Details
//                                     </Typography>
//                                     <Divider sx={{ marginBottom: "24px" }} />
//                                     <Grid container spacing={4}>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Fine Tuning Job Name" />
//                                             <TextField fullWidth name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Enter a name for your fine-tuning job" variant="filled" hiddenLabel error={touched.name && Boolean(errors.name)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="name" />
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Application" />
//                                             <TextField select fullWidth name="application" value={values.application} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.application && Boolean(errors.application)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                 {applicationNamesList.map(appName => (<MenuItem key={appName} value={appName}>{appName}</MenuItem>))}
//                                             </TextField>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="application" />
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Base Model to Fine Tune" />
//                                             <TextField select fullWidth name="baseModel" value={values.baseModel} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.baseModel && Boolean(errors.baseModel)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                 {baseModelList.map((option) => (
//                                                     <MenuItem key={option.value} value={option.value}>
//                                                         {option.label}
//                                                     </MenuItem>
//                                                 ))}
//                                             </TextField>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="baseModel" />
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Tuning Method" />
//                                             <TextField select fullWidth name="tuningMethod" value={values.tuningMethod} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.tuningMethod && Boolean(errors.tuningMethod)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                 {tuningMethodList.map((option) => (
//                                                     <MenuItem key={option.value} value={option.value}>
//                                                         {option.label}
//                                                     </MenuItem>
//                                                 ))}
//                                             </TextField>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="tuningMethod" />
//                                             </div>
//                                         </Grid>
//                                     </Grid>
//                                 </div>

//                                 {/* --- Labels Section --- */}
//                                 <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                     <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Labels</Typography>
//                                     <Divider sx={{ mb: "24px" }} />
//                                     <Grid container spacing={3} sx={{ mb: "16px" }}>
//                                         <Grid item xs={12} sm={6}>
//                                             <Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography>
//                                             <TextField fullWidth value="cost-center" disabled variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px", backgroundColor: "#f0f0f0" } }} />
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Value (Cost Center)" />
//                                             <TextField fullWidth name="costCenterValue" value={values.costCenterValue} onChange={handleChange} onBlur={handleBlur} placeholder="Enter cost center value" variant="filled" hiddenLabel error={touched.costCenterValue && Boolean(errors.costCenterValue)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" /></div>
//                                         </Grid>
//                                     </Grid>
//                                     <FieldArray
//                                         name="costLabelPairs"
//                                         render={arrayHelpers => (
//                                             <div>
//                                                 {values.costLabelPairs.map((pair, index) => (
//                                                     <Grid container spacing={2} key={pair.id || index} sx={{ mb: "16px" }} alignItems="center">
//                                                         <Grid item xs={5}>
//                                                             <CompulsoryFieldsLabel fieldName="Key" />
//                                                             <TextField fullWidth name={`costLabelPairs.${index}.cost`} value={pair.cost} onChange={handleChange} onBlur={handleBlur} placeholder="Enter key" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].cost`) && !!getIn(errors, `costLabelPairs[${index}].cost`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                             <div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.cost`} /></div>
//                                                         </Grid>
//                                                         <Grid item xs={6}>
//                                                             <CompulsoryFieldsLabel fieldName="Value" />
//                                                             <TextField fullWidth name={`costLabelPairs.${index}.label`} value={pair.label} onChange={handleChange} onBlur={handleBlur} placeholder="Enter value" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].label`) && !!getIn(errors, `costLabelPairs[${index}].label`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                             <div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.label`} /></div>
//                                                         </Grid>
//                                                         <Grid item xs={1}><IconButton onClick={() => arrayHelpers.remove(index)} sx={{ mt: 2 }}><img alt="remove" src="/assets/close-icon.svg" /></IconButton></Grid>
//                                                     </Grid>
//                                                 ))}
//                                                 <Button variant="text" sx={{ color: "#000000", textTransform: "none" }} onClick={() => arrayHelpers.push({ cost: "", label: "", id: `new-${Math.random()}` })}>+ Add Key Value Pair</Button>
//                                             </div>
//                                         )}
//                                     />
//                                 </div>


//                                 {/* --- Dataset Configuration Section --- */}
//                                 <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                     <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
//                                         Dataset Configuration
//                                     </Typography>
//                                     <Divider sx={{ marginBottom: "24px" }} />
//                                     <Grid container spacing={4}>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Training Data Path" />
//                                             <TextField fullWidth name="trainingDataPath" value={values.trainingDataPath} onChange={handleChange} onBlur={handleBlur} placeholder="gs://your-bucket/path/to/training-data.jsonl" variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="trainingDataPath" />
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <Typography fontFamily="Gilroy-Regular" fontSize="14px" color="gray" ml="4px" mb="4px">Validation Data Path (optional)</Typography>
//                                             <TextField fullWidth name="validationDataPath" value={values.validationDataPath} onChange={handleChange} onBlur={handleBlur} placeholder="gs://your-bucket/path/to/validation-data.jsonl" variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Data Format Template" />
//                                             <TextField select fullWidth name="dataFormatTemplate" value={values.dataFormatTemplate} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                 {dataFormatTemplateList.map((option) => (
//                                                     <MenuItem key={option.value} value={option.value}>
//                                                         {option.label}
//                                                     </MenuItem>
//                                                 ))}
//                                             </TextField>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="dataFormatTemplate" />
//                                             </div>
//                                         </Grid>
//                                     </Grid>
//                                 </div>

//                                 {/* --- Training Infrastructure and Hardware Section --- */}
//                                 <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                     <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
//                                         Training Infrastructure and Hardware
//                                     </Typography>
//                                     <Divider sx={{ marginBottom: "24px" }} />
//                                     <Grid container spacing={4}>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Training Region" />
//                                             <TextField select fullWidth name="trainingRegion" value={values.trainingRegion} onChange={handleChange} onBlur={handleBlur} disabled={!values.cloudProvider} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                 {values.cloudProvider && trainingRegionList[values.cloudProvider as keyof typeof trainingRegionList]?.map((option) => (
//                                                     <MenuItem key={option.value} value={option.value}>
//                                                         {option.label}
//                                                     </MenuItem>
//                                                 ))}
//                                             </TextField>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="trainingRegion" />
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Accelerator (GPU) Type" />
//                                             <TextField select fullWidth name="acceleratorType" value={values.acceleratorType} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                 {acceleratorTypeList.map((option) => (
//                                                     <MenuItem key={option.value} value={option.value}>
//                                                         {option.label}
//                                                     </MenuItem>
//                                                 ))}
//                                             </TextField>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="acceleratorType" />
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Accelerator Count" />
//                                             <TextField fullWidth type="number" name="acceleratorCount" value={values.acceleratorCount} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="acceleratorCount" />
//                                             </div>
//                                         </Grid>
//                                     </Grid>
//                                 </div>


//                                 {/* --- Hyperparameters Section --- */}
//                                 <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                     <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
//                                         Hyperparameters (Advanced Tuning)
//                                     </Typography>
//                                     <Divider sx={{ marginBottom: "24px" }} />
//                                     <Grid container spacing={4}>
//                                         <Grid item xs={12} sm={4}>
//                                             <CompulsoryFieldsLabel fieldName="Epochs" />
//                                             <TextField fullWidth type="number" name="epochs" value={values.epochs} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="epochs" />
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={4}>
//                                             <CompulsoryFieldsLabel fieldName="Learning Rate" />
//                                             <TextField fullWidth type="number" name="learningRate" value={values.learningRate} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="learningRate" />
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={4}>
//                                             <CompulsoryFieldsLabel fieldName="Batch Size" />
//                                             <TextField fullWidth type="number" name="batchSize" value={values.batchSize} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="batchSize" />
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="LoRA Rank (r)" />
//                                             <TextField fullWidth type="number" name="loraRank" value={values.loraRank} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="loraRank" />
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="LoRA Alpha (α)" />
//                                             <TextField fullWidth type="number" name="loraAlpha" value={values.loraAlpha} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="loraAlpha" />
//                                             </div>
//                                         </Grid>
//                                     </Grid>
//                                 </div>


//                                 {/* --- Action Buttons --- */}
//                                 <FormControlLabel
//                                     control={<Checkbox checked={confirmationChecked} onChange={(e) => setConfirmationChecked(e.target.checked)} name="confirmationCheckbox" sx={{ color: 'black', '&.Mui-checked': { color: 'black' } }} />}
//                                     label={<Typography sx={{ color: 'black' }}>I confirm that the details provided are correct and I wish to proceed.</Typography>}
//                                     sx={{ mt: 2.5, display: 'flex' }}
//                                 />
//                                 <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
//                                     <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, }} variant="outlined" size="large" onClick={() => navigate("/aisolutions/finetuning")}>
//                                         Cancel
//                                     </Button>
//                                     <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "white", color: "black", borderColor: "black", }, textTransform: "none", px: 4, }} variant="contained" size="large" type="submit" disabled={!(isValid && dirty && confirmationChecked) || isViewer}>
//                                         {isEdit ? "Save Changes" : "Create Fine Tuning Job"}
//                                     </Button>
//                                 </Box>
//                             </Grid>
//                             <Grid item xs={1}></Grid>
//                         </Grid>
//                         <MiniBot />
//                     </Form>
//                 )}
//             </Formik>
//         </Container>
//     );
// };

// const FineTuningForm: React.FC<FineTuningFormProps> = ({ isEdit }) => {
//     const theme = useTheme();

//     const getInitialDrawerState = () => {
//         if (typeof window !== 'undefined') {
//             const storedState = localStorage.getItem('isFaradayDrawerOpen');
//             return storedState !== null ? JSON.parse(storedState) : true;
//         }
//         return true;
//     };
//     const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

//     const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
//         setIsDrawerActuallyOpen(isOpenFromDrawer);
//     };

//     const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer
//                 index={"aisolutions-finetuning"}
//                 onStateChange={handleDrawerStateChange}
//             />
//             <Box
//                 component="main"
//                 sx={{
//                     flexGrow: 1,
//                     p: 0,
//                     transition: theme.transitions.create('margin', {
//                         easing: theme.transitions.easing.sharp,
//                         duration: theme.transitions.duration.enteringScreen,
//                     }),
//                     marginLeft: `${currentMarginLeft}vh`,
//                 }}
//             >
//                 <FineTuningFormContent isEdit={isEdit} />
//             </Box>
//         </Box>
//     );
// };

// export default FineTuningForm;


// import React, { useState } from "react";
// import {
//     Box,
//     Button,
//     Container,
//     Grid,
//     TextField,
//     Typography,
//     MenuItem,
//     IconButton,
//     Breadcrumbs,
//     Link,
//     Divider,
//     useTheme,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import { useNavigate } from "react-router-dom";
// import { Formik, Form, ErrorMessage } from "formik";
// import * as Yup from "yup";
// import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
// import { useAuth } from "../../../helpers/AuthProvider";
// import MiniBot from "../../MiniBot";
// import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer"; // Adjust path if needed

// // --- Configuration ---

// const cloudProviderList = [
//     { value: "gcp", label: "Google Cloud" },
//     // Other cloud providers can be added here in the future
// ];

// const baseModelList = [
//     { value: "google/gemma-7b-it", label: "google/gemma-7b-it" },
//     { value: "meta-llama/Llama-3-8B-Instruct", label: "meta-llama/Llama-3-8B-Instruct" },
//     // Other base models can be added here
// ];

// const tuningMethodList = [
//     { value: "lora", label: "LoRA" },
//     { value: "qlora", label: "QLoRA" },
// ];

// const dataFormatTemplateList = [
//     { value: "conversational", label: "Conversational" },
//     { value: "instruction", label: "Instruction" },
// ];

// const trainingRegionList = {
//     gcp: [
//         { value: "asia-east1", label: "asia-east1" },
//         { value: "asia-east2", label: "asia-east2" },
//         { value: "asia-northeast1", label: "asia-northeast1" },
//         { value: "asia-northeast2", label: "asia-northeast2" },
//         { value: "asia-northeast3", label: "asia-northeast3" },
//         { value: "asia-south1", label: "asia-south1" },
//         { value: "asia-south2", label: "asia-south2" },
//         { value: "asia-southeast1", label: "asia-southeast1" },
//         { value: "asia-southeast2", label: "asia-southeast2" },
//         { value: "australia-southeast1", label: "australia-southeast1" },
//         { value: "australia-southeast2", label: "australia-southeast2" },
//         { value: "europe-central2", label: "europe-central2" },
//         { value: "europe-north1", label: "europe-north1" },
//         { value: "europe-southwest1", label: "europe-southwest1" },
//         { value: "europe-west1", label: "europe-west1" },
//         { value: "europe-west2", label: "europe-west2" },
//         { value: "europe-west3", label: "europe-west3" },
//         { value: "europe-west4", label: "europe-west4" },
//         { value: "europe-west6", label: "europe-west6" },
//         { value: "europe-west8", label: "europe-west8" },
//         { value: "europe-west9", label: "europe-west9" },
//         { value: "europe-west12", label: "europe-west12" },
//         { value: "me-central1", label: "me-central1" },
//         { value: "me-central2", label: "me-central2" },
//         { value: "me-west1", label: "me-west1" },
//         { value: "northamerica-northeast1", label: "northamerica-northeast1" },
//         { value: "northamerica-northeast2", label: "northamerica-northeast2" },
//         { value: "southamerica-east1", label: "southamerica-east1" },
//         { value: "southamerica-west1", label: "southamerica-west1" },
//         { value: "us-central1", label: "us-central1" },
//         { value: "us-east1", label: "us-east1" },
//         { value: "us-east4", label: "us-east4" },
//         { value: "us-east5", label: "us-east5" },
//         { value: "us-south1", label: "us-south1" },
//         { value: "us-west1", label: "us-west1" },
//         { value: "us-west2", label: "us-west2" },
//         { value: "us-west3", label: "us-west3" },
//         { value: "us-west4", label: "us-west4" },
//     ],
// };

// const acceleratorTypeList = [
//     { value: "NVIDIA_L4", label: "NVIDIA_L4" },
//     { value: "NVIDIA_TESLA_T4", label: "NVIDIA_TESLA_T4" },
//     { value: "NVIDIA_A100_40GB", label: "NVIDIA_A100_40GB" },
// ];


// // --- Validation Schema ---

// const validationSchema = Yup.object().shape({
//     cloudProvider: Yup.string().required("This field is required"),
//     fineTuningJobName: Yup.string().required("This field is required"),
//     baseModel: Yup.string().required("This field is required"),
//     tuningMethod: Yup.string().required("This field is required"),
//     trainingDataPath: Yup.string().required("This field is required"),
//     validationDataPath: Yup.string(),
//     dataFormatTemplate: Yup.string().required("This field is required"),
//     trainingRegion: Yup.string().required("This field is required"),
//     acceleratorType: Yup.string().required("This field is required"),
//     acceleratorCount: Yup.number().integer("Must be an integer").required("This field is required"),
//     epochs: Yup.number().integer("Must be an integer").required("This field is required"),
//     learningRate: Yup.number().required("This field is required"),
//     batchSize: Yup.number().integer("Must be an integer").required("This field is required"),
//     loraRank: Yup.number().integer("Must be an integer").required("This field is required"),
//     loraAlpha: Yup.number().integer("Must be an integer").required("This field is required"),
// });

// interface FineTuningFormProps {
//     isEdit: boolean;
// }
// interface FineTuningFormContentProps {
//     isEdit: boolean;
// }

// const FineTuningFormContent: React.FC<FineTuningFormContentProps> = ({ isEdit }) => {
//     const navigate = useNavigate();
//     const { user } = useAuth(); // Assuming useAuth provides user info

//     return (
//         <Container
//             disableGutters
//             maxWidth={false}
//             sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}
//         >
//             <MiniBot />
//             <Box sx={{ mb: 4 }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
//                     <Link underline="hover" color="inherit" href="/aisolutions/finetuning">
//                         Fine Tuning Jobs
//                     </Link>
//                     <Typography color="text.primary">
//                         {isEdit ? "Edit Fine Tuning Job" : "New Fine Tuning Job"}
//                     </Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" mt={2}>
//                     <IconButton
//                         edge="start"
//                         aria-label="back"
//                         color="inherit"
//                         onClick={() => navigate("/aisolutions/finetuning")}
//                     >
//                         <ArrowBackIosNewIcon fontSize="small" />
//                     </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="18px">
//                         {isEdit ? "Edit Fine Tuning Job" : "Add a New Fine Tuning Job"}
//                     </Typography>
//                 </Box>
//             </Box>
//             <Grid container>
//                 <Grid item xs={1}></Grid>
//                 <Grid item xs={10}>
//                     <Formik
//                         initialValues={{
//                             cloudProvider: "gcp",
//                             fineTuningJobName: "",
//                             baseModel: "",
//                             tuningMethod: "",
//                             trainingDataPath: "",
//                             validationDataPath: "",
//                             dataFormatTemplate: "",
//                             trainingRegion: "",
//                             acceleratorType: "",
//                             acceleratorCount: 1,
//                             epochs: 3,
//                             learningRate: 0.0002,
//                             batchSize: 4,
//                             loraRank: 4,
//                             loraAlpha: 16,
//                         }}
//                         validationSchema={validationSchema}
//                         onSubmit={(values) => {
//                             // Form is static, so submission is disabled.
//                             console.log("Form submission is disabled.", values);
//                         }}
//                     >
//                         {({
//                             handleChange,
//                             handleBlur,
//                             values,
//                             setFieldValue,
//                             errors,
//                             touched,
//                         }) => (
//                             <Form>
//                                  <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                     <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
//                                         Cloud Provider
//                                     </Typography>
//                                     <Divider sx={{ marginBottom: "24px" }} />
//                                      <Grid container spacing={4}>
//                                         <Grid item xs={12} sm={12}>
//                                             <CompulsoryFieldsLabel fieldName="Cloud Provider" />
//                                             <TextField
//                                                 select
//                                                 fullWidth
//                                                 name="cloudProvider"
//                                                 value={values.cloudProvider}
//                                                 onChange={handleChange}
//                                                 onBlur={handleBlur}
//                                                 variant="filled"
//                                                 hiddenLabel
//                                                 InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}
//                                             >
//                                                 {cloudProviderList.map((option) => (
//                                                     <MenuItem key={option.value} value={option.value}>
//                                                         {option.label}
//                                                     </MenuItem>
//                                                 ))}
//                                             </TextField>
//                                              <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="cloudProvider"/>
//                                             </div>
//                                         </Grid>
//                                     </Grid>
//                                  </div>


//                                 {/* --- Fine Tuning Job Details Section --- */}
//                                 <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                     <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
//                                         Fine Tuning Job Details
//                                     </Typography>
//                                     <Divider sx={{ marginBottom: "24px" }} />
//                                     <Grid container spacing={4}>
//                                         <Grid item xs={12}>
//                                             <CompulsoryFieldsLabel fieldName="Fine Tuning Job Name" />
//                                             <TextField fullWidth name="fineTuningJobName" value={values.fineTuningJobName} onChange={handleChange} onBlur={handleBlur} placeholder="Enter a name for your fine-tuning job" variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="fineTuningJobName"/>
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Base Model to Fine Tune" />
//                                             <TextField select fullWidth name="baseModel" value={values.baseModel} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                 {baseModelList.map((option) => (
//                                                     <MenuItem key={option.value} value={option.value}>
//                                                         {option.label}
//                                                     </MenuItem>
//                                                 ))}
//                                             </TextField>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="baseModel"/>
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Tuning Method" />
//                                             <TextField select fullWidth name="tuningMethod" value={values.tuningMethod} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                 {tuningMethodList.map((option) => (
//                                                     <MenuItem key={option.value} value={option.value}>
//                                                         {option.label}
//                                                     </MenuItem>
//                                                 ))}
//                                             </TextField>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="tuningMethod"/>
//                                             </div>
//                                         </Grid>
//                                     </Grid>
//                                 </div>


//                                 {/* --- Dataset Configuration Section --- */}
//                                 <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                      <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
//                                         Dataset Configuration
//                                     </Typography>
//                                     <Divider sx={{ marginBottom: "24px" }} />
//                                      <Grid container spacing={4}>
//                                          <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Training Data Path" />
//                                             <TextField fullWidth name="trainingDataPath" value={values.trainingDataPath} onChange={handleChange} onBlur={handleBlur} placeholder="gs://your-bucket/path/to/training-data.jsonl" variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
//                                              <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="trainingDataPath"/>
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <Typography fontFamily="Gilroy-Regular" fontSize="14px" color="gray" ml="4px" mb="4px">Validation Data Path (optional)</Typography>
//                                             <TextField fullWidth name="validationDataPath" value={values.validationDataPath} onChange={handleChange} onBlur={handleBlur} placeholder="gs://your-bucket/path/to/validation-data.jsonl" variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
//                                         </Grid>
//                                          <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Data Format Template" />
//                                             <TextField select fullWidth name="dataFormatTemplate" value={values.dataFormatTemplate} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                 {dataFormatTemplateList.map((option) => (
//                                                     <MenuItem key={option.value} value={option.value}>
//                                                         {option.label}
//                                                     </MenuItem>
//                                                 ))}
//                                             </TextField>
//                                              <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="dataFormatTemplate"/>
//                                             </div>
//                                         </Grid>
//                                     </Grid>
//                                 </div>

//                                 {/* --- Training Infrastructure and Hardware Section --- */}
//                                 <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                     <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
//                                         Training Infrastructure and Hardware
//                                     </Typography>
//                                     <Divider sx={{ marginBottom: "24px" }} />
//                                     <Grid container spacing={4}>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Training Region" />
//                                             <TextField select fullWidth name="trainingRegion" value={values.trainingRegion} onChange={handleChange} onBlur={handleBlur} disabled={!values.cloudProvider} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                  {values.cloudProvider && trainingRegionList[values.cloudProvider as keyof typeof trainingRegionList]?.map((option) => (
//                                                         <MenuItem key={option.value} value={option.value}>
//                                                             {option.label}
//                                                         </MenuItem>
//                                                     ))}
//                                             </TextField>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="trainingRegion"/>
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Accelerator (GPU) Type" />
//                                             <TextField select fullWidth name="acceleratorType" value={values.acceleratorType} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                 {acceleratorTypeList.map((option) => (
//                                                     <MenuItem key={option.value} value={option.value}>
//                                                         {option.label}
//                                                     </MenuItem>
//                                                 ))}
//                                             </TextField>
//                                              <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="acceleratorType"/>
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="Accelerator Count" />
//                                             <TextField fullWidth type="number" name="acceleratorCount" value={values.acceleratorCount} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="acceleratorCount"/>
//                                             </div>
//                                         </Grid>
//                                     </Grid>
//                                 </div>


//                                 {/* --- Hyperparameters Section --- */}
//                                 <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                     <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
//                                         Hyperparameters (Advanced Tuning)
//                                     </Typography>
//                                     <Divider sx={{ marginBottom: "24px" }} />
//                                     <Grid container spacing={4}>
//                                         <Grid item xs={12} sm={4}>
//                                             <CompulsoryFieldsLabel fieldName="Epochs" />
//                                             <TextField fullWidth type="number" name="epochs" value={values.epochs} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="epochs"/>
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={4}>
//                                             <CompulsoryFieldsLabel fieldName="Learning Rate" />
//                                             <TextField fullWidth type="number" name="learningRate" value={values.learningRate} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="learningRate"/>
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={4}>
//                                             <CompulsoryFieldsLabel fieldName="Batch Size" />
//                                             <TextField fullWidth type="number" name="batchSize" value={values.batchSize} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="batchSize"/>
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="LoRA Rank (r)" />
//                                             <TextField fullWidth type="number" name="loraRank" value={values.loraRank} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
//                                              <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="loraRank"/>
//                                             </div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="LoRA Alpha (α)" />
//                                             <TextField fullWidth type="number" name="loraAlpha" value={values.loraAlpha} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
//                                              <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                 <ErrorMessage name="loraAlpha"/>
//                                             </div>
//                                         </Grid>
//                                     </Grid>
//                                 </div>


//                                 {/* --- Action Buttons --- */}
//                                 <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
//                                     <Button
//                                         sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, }}
//                                         variant="outlined"
//                                         size="large"
//                                         onClick={() => navigate("/finetuning/jobs")}
//                                     >
//                                         Cancel
//                                     </Button>
//                                     <Button
//                                         sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "white", color: "black", borderColor: "black", }, textTransform: "none", px: 4, }}
//                                         variant="contained"
//                                         size="large"
//                                         type="submit"
//                                         disabled={true} // Statically disabled
//                                     >
//                                         {isEdit ? "Save Changes" : "Add New Fine Tuning Job"}
//                                     </Button>
//                                 </Box>
//                             </Form>
//                         )}
//                     </Formik>
//                 </Grid>
//                 <Grid item xs={1}></Grid>
//             </Grid>
//         </Container>
//     );
// };

// const FineTuningForm: React.FC<FineTuningFormProps> = ({ isEdit }) => {
//     const theme = useTheme();

//     const getInitialDrawerState = () => {
//         if (typeof window !== 'undefined') {
//             const storedState = localStorage.getItem('isFaradayDrawerOpen');
//             return storedState !== null ? JSON.parse(storedState) : true;
//         }
//         return true;
//     };
//     const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

//     const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
//         setIsDrawerActuallyOpen(isOpenFromDrawer);
//     };

//     const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer
//                 index={"aisolutions-finetuning"} // Example index, adjust as needed
//                 onStateChange={handleDrawerStateChange}
//             />
//             <Box
//                 component="main"
//                 sx={{
//                     flexGrow: 1,
//                     p: 0,
//                     transition: theme.transitions.create('margin', {
//                         easing: theme.transitions.easing.sharp,
//                         duration: theme.transitions.duration.enteringScreen,
//                     }),
//                     marginLeft: `${currentMarginLeft}vh`,
//                 }}
//             >
//                 <FineTuningFormContent isEdit={isEdit} />
//             </Box>
//         </Box>
//     );
// };

// export default FineTuningForm;